import { TempSettingsXops } from "app";
import { flightEntityDescriptor, humanResourceEntityDescriptor, humanResourceScheduleEntityDescriptor, missionEntityDescriptor, taskEntityDescriptor } from "AppEntityDescriptors";
import { AppContainer, Utils } from "@crispico/foundation-react";
import { XopsAppContainerContextValue } from "XopsAppContainerContext";
import { AssignTasksToResourcePageProps, MissionStatusGroup } from "./AssignTasksToResourcePage";
import { Mission2EntityDescriptor } from "pages/Mission2/Mission2EntityDescriptor";
import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { EquipmentResource, Flight, HumanResource, HumanResourceSchedule, isMissionFinished, Mission, Task } from "types";
import { Identifier } from "./DragAndDrop";
import { equipmentResourceEntityDescriptor } from "pages/EquipmentResource/equipmentResourceEntityDescriptor";

const mapAssignTasksToResourcePagePropsFields: { [filed: string]: { [entityName: string]: string } } = {
    "equipmentResourceId": {
        "EquipmentResource": "id",
        "HumanResourceSchedule": "equipmentResource.id",
        "Mission2": "equipmentResource.id"
    },
    "humanResourceId": {
        "EquipmentResource": "humanResource.id",
        "HumanResourceSchedule": "humanResource.id",
        "Mission2": "humanResource.id"
    },
    "missionId": {
        "Mission2": "id"
    },
    "taskIds": {
        "Task": "id",
        "Flight": "tasks"
    }
}

export type ItemEntity = Flight | Task | Mission | HumanResourceSchedule | EquipmentResource;

export class DragAndDropControllerNew {

    private static _INSTANCE: DragAndDropControllerNew;

    static get INSTANCE(): DragAndDropControllerNew {
        if (!DragAndDropControllerNew._INSTANCE) {
            DragAndDropControllerNew._INSTANCE = new DragAndDropControllerNew();
        }
        return DragAndDropControllerNew._INSTANCE;
    }

    private getFieldValue(fieldName: string, sourceEntityName: string, source: ItemEntity, destinationEntityName: string, destination: ItemEntity) {
        let field;
        try {
            field = Utils.navigate(source, mapAssignTasksToResourcePagePropsFields[fieldName][sourceEntityName], true, ".");
        } catch (e) {
            if (!field) {
                try {
                    field = Utils.navigate(destination, mapAssignTasksToResourcePagePropsFields[fieldName][destinationEntityName], true, ".");
                } catch {
                    // nop
                }
            }
        }
        return field;
    }

    public getAcceptedType(entityName: string) {
        switch (entityName) {
            case "Flight":
            case "Task":
                return ["Mission2", "HumanResourceSchedule", "EquipmentResource"];
            case "HumanResourceSchedule":
            case "EquipmentResource":
                return ["Flight", "Task"];
            case "Mission2":
                return ["Flight", "Task"];
            default:
                return ["Mission2", "Flight", "Task", "HumanResourceSchedule", "EquipmentResource"];
        }
    }

    public renderDragLayer(entities: ItemEntity[], entityType: Identifier) {
        if (!entities || !entities.length) {
            return null;
        }
        return <div className="flex-container"> {
            entities.map(entity => {
                return <div className="flex-container-row" key={entity.id}>{entityDescriptors[entityType as string].toMiniString(entity)}</div>;
            })
        }
        </div>;
    }

    /**
     * Task: unassigned
     * Flight: with at least one unassigned task.
     * Mission: not "finished"({@link Mission2EntityDescriptor.isFinished})
     * EquipmentResource: unarchived
     * HumanResourceSchedule: not finished
     * 
     * Multiple selection only for tasks(all need to be unassigned)
     */
    public canDrag(entities: ItemEntity[], entityType: Identifier): boolean {
        if (!entities || !entities.length || (entities.length > 1 && entityType != taskEntityDescriptor.name)) {
            return false;
        }

        return entities.reduce((result: boolean, entity: ItemEntity) => {
            switch (entityType) {
                case flightEntityDescriptor.name: {
                    entity = entity as Flight;
                    result = result && entity.tasks && entity.tasks.some((task: Omit<Task, 'taskGroup'>) => !task.mission);
                    break;
                }
                case taskEntityDescriptor.name: {
                    entity = entity as Task;
                    result = result && !entity.mission;
                    break;
                }
                case missionEntityDescriptor.name: {
                    entity = entity as Mission;
                    result = result && !isMissionFinished(entity);
                    break;
                }
                case equipmentResourceEntityDescriptor.name: {
                    entity = entity as EquipmentResource;
                    result = result && !(entity.archived ?? false);
                    break;
                }
                case humanResourceScheduleEntityDescriptor.name: {
                    entity = entity as HumanResourceSchedule;
                    result = result && !entity.finished;
                    break;
                }
                default:
                    result = false;
                    break;
            }
            return result;
        }, true);
    }

    /**
     * same as {@link canDrag} with additional condition for a human resource if is borrowed to unit.
     */
    public canDrop(sources: ItemEntity[], sourceEntityType: Identifier, destination: ItemEntity, destinationEntityType: Identifier) {
        const source = sources[0];

        if (!source || !destination) {
            return false;
        }

        if (destinationEntityType == missionEntityDescriptor.name || destinationEntityType == humanResourceEntityDescriptor.name) {
            destination = destination as Mission | HumanResourceSchedule;
            const { currentOrganization, tempSettings } = (AppContainer.INSTANCE.getAppContainerContextValue() as XopsAppContainerContextValue).initializationsForClient;
            if ((tempSettings as unknown as TempSettingsXops)?.tempUnitSettings?.length) {
                const hr: HumanResource = destination.humanResource;
                if (hr && currentOrganization && hr.unit.name != currentOrganization.name) {
                    if (!hr.borrowedToUnit || hr.borrowedToUnit.name != currentOrganization.name) {
                        return false;
                    }
                }
            }
        }
        return DragAndDropControllerNew.INSTANCE.canDrag([destination], destinationEntityType);
    }

    public getAssignTasksToResourcePageProps(sources: ItemEntity[], sourceEntityType: Identifier, destination: ItemEntity, destinationEntityType: Identifier): AssignTasksToResourcePageProps | undefined {
        const source = sources[0];
        if (!source || !destination) {
            // if we don't have the information about entity(destination/source) nop.
            return undefined;
        }

        let taskIds = DragAndDropControllerNew.INSTANCE.getFieldValue("taskIds", sourceEntityType as string, source, destinationEntityType as string, destination);
        if (sources.length > 1) {
            // only for task can drag multiple items
            taskIds = []
            sources.forEach((task: ItemEntity) => {
                taskIds.push(task.id);
            })
        } else if (Array.isArray(taskIds)) {
            taskIds = taskIds.map(t => t.id);
        } else {
            taskIds = [taskIds];
        }
        let missionId = DragAndDropControllerNew.INSTANCE.getFieldValue("missionId", sourceEntityType as string, source, destinationEntityType as string, destination);
        let humanResourceId = DragAndDropControllerNew.INSTANCE.getFieldValue("humanResourceId", sourceEntityType as string, source, destinationEntityType as string, destination);
        let equipmentResourceId = DragAndDropControllerNew.INSTANCE.getFieldValue("equipmentResourceId", sourceEntityType as string, source, destinationEntityType as string, destination);
        let defaultMissionStatusGroup = sourceEntityType == missionEntityDescriptor.name || destinationEntityType == missionEntityDescriptor.name ? MissionStatusGroup.EXISTING : undefined

        return { humanResourceId, missionId, taskIds, equipmentResourceId, initialSelectedTaskIds: taskIds, defaultMissionStatusGroup };
    }
}
